// Food catalog — Body module, DefineHoly
// Curated, hand-authored, baked into the bundle. Same call as recipeData.js.
//
// SCHEMA NOTES (supersedes BODY_Food_Spec_v1.md §3):
//   - `basis` is the ONLY basis field. It states what you put on the scale.
//     Values: 'cooked' | 'as-is' | 'as-added'
//   - `nutritionBasis` and `yield` are DELETED. They existed to convert raw
//     weight to cooked nutrition. We weigh cooked, so there is nothing to
//     convert. See amendment note in session docs.
//   - All per100g values describe 100g of the thing AS WEIGHED.
//   - UNITS, per nutrient — not one blanket rule:
//       sodium, potassium, magnesium, calcium  -> mg
//       iodine, retinol                        -> mcg
//     The per100g KEYS stay unit-free; the Food_Log column each maps to carries
//     the unit in its name where it is not mg (IodineMcg, RetinolMcg). See
//     PER100G_TO_COLUMN in foodEntry.js, which is the only place the two
//     vocabularies meet.
//   - `null` means not disclosed by the source — NOT zero. The daily view must
//     distinguish the two or totals will read low. An ABSENT key means the same
//     thing and is the normal state for a nutrient nobody has sourced yet.
//   - `confidence`: 'verified' (table read directly), 'good' (secondary
//     source, minor ambiguity noted), 'estimated' (derived — see note).
//     Anything not 'verified' logs as EntrySource: 'Estimated'.
//   - `boneWeightPerUnit` (grams) present only where bone rides the scale.
//     App subtracts count × this from plate weight.
//
// MICRONUTRIENTS DELIBERATELY ABSENT on nine entries — ribeye, ny-strip,
// bacon, canadian-bacon-homemade, jerky-homemade, costco-beef-stick,
// french-fries-tallow, chicken-stock and chicken-stock-reduction. These are
// scattered across four categories
// rather than grouped, which is why this note lives up here instead of above
// them.
//
// They carry no iodine, calcium or retinol key at all. That is a decision, not
// an oversight, and the two reasons are different:
//
//   CALCIUM AND RETINOL — the available sources either conflicted with each
//   other or described a different food than the entry. The bacon figure that
//   is easy to find describes rendered bacon FAT, not cured slices; using it
//   would attach a number to the wrong substance. A wrong number is worse than
//   no number here, because an absent key reads as "not known" all the way
//   through to the daily total, while a wrong one reads as fact forever.
//
//   IODINE — absent on every meat entry in this catalog because USDA does not
//   measure it. Full FDC panels carrying 40+ fields down to fluoride and
//   theobromine contain no iodine row at all. It is not that these foods have
//   none; it is that nobody sampled for it. Where iodine IS declared — iodized
//   salt — it is a label figure, not a USDA one.
//
// Filling these in later is a per-entry change and nothing else: add the key,
// and the row starts carrying the column. No logic moves.

export const categories = ['Beef', 'Poultry', 'Fish', 'Pork', 'Eggs', 'Fats', 'Dairy', 'Broth', 'Electrolytes', 'Plants', 'Off-Plan'];

const foodData = [

  // ---------- BEEF ----------
  {
    id: 'ground-beef-80-20',
    name: 'Ground Beef 80/20',
    category: 'Beef',
    basis: 'cooked',
    confidence: 'verified',
    // USDA cooked 80/20, BROILED. The pan-broiled record reads calcium 26;
    // broiled is taken here because it is the same preparation the macros above
    // came from. Noting the alternative so the 2mg gap is not mistaken later
    // for a transcription slip. No iodine: USDA does not measure it.
    per100g: { kcal: 272, protein: 27, fat: 17.4, carbs: 0,
               sodium: 91, potassium: 380, magnesium: 23,
               calcium: 24, retinol: 3 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
      { unit: 'lb', gramsPerUnit: 453.6 },
    ],
    defaultUnit: 'g',
    defaultQty: 170,
    source: 'USDA FDC #171799 — beef, ground, 80% lean/20% fat, crumbles, cooked, pan-browned',
    note: 'Restaurant Depot. Weigh after cooking; fat left in the pan is not on the scale.',
  },

  {
    id: 'taco-meat-fragos',
    name: 'Fragos Taco Meat (finished)',
    category: 'Beef',
    basis: 'cooked',
    confidence: 'estimated',
    per100g: { kcal: 311, protein: 23, fat: 23, carbs: 3.6,
               sodium: 476, potassium: 400, magnesium: 34, calcium: 55,
               retinol: 3 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'g',
    defaultQty: 100,
    source: 'Computed from Fragos Taco Meat v1.1 at 0.89 scale. Pan totals: 3422 cal, 250 g protein, 250 g fat, 40 g carbs, 5240 mg sodium, 4400 mg potassium, 370 mg magnesium, 600 mg calcium. Divided by an ASSUMED 1100 g finished weight.',
    note: 'ESTIMATED - the finished pan has never been weighed. 1100 g is a guess; the real value likely falls 1000-1200 g, which swings this entry +/-8%. TO CORRECT: weigh the finished pan, divide each pan total in source by that weight, multiply by 100. Beef is UNDRAINED so retained rendered fat is included - this is why the figure sits far above the 272 cal/100g of drained ground-beef-80-20, and why logging a taco plate as plain ground beef understates it badly. Recipe salt (11 g) IS included in the sodium above; log any additional salt via the percentage stepper. Stock protein assumes 10 g/100 g for a hard-gelling wing stock - the softest number here. Iodine ABSENT. Potassium, magnesium, calcium and retinol are carried from the beef and spice components, not measured.',
  },

  {
    id: 'ribeye',
    name: 'Ribeye Steak',
    category: 'Beef',
    basis: 'cooked',
    confidence: 'good',
    per100g: { kcal: 291, protein: 24, fat: 22, carbs: 0,
               sodium: 54, potassium: 260, magnesium: 22 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'g',
    defaultQty: 280,
    source: 'USDA SR — rib eye, boneless, lip-on, lean and fat, trimmed to 1/8", all grades, cooked, grilled',
    note: 'Boneless. TRIM AMBIGUITY: USDA carries 0" and 1/8" trim records that differ by ~30% on fat (265–291 kcal/100g). 1/8" lip-on chosen as closest to a normal marbled ribeye. Confirm against FDC before treating as exact.',
  },

  {
    id: 'ny-strip',
    name: 'NY Strip Steak',
    category: 'Beef',
    basis: 'cooked',
    confidence: 'good',
    per100g: { kcal: 243, protein: 27, fat: 15, carbs: 0,
               sodium: 55, potassium: 320, magnesium: 24 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'g',
    defaultQty: 280,
    source: 'USDA SR — beef short loin, strip steak, lean and fat, cooked, grilled',
    note: 'Boneless. Leaner than ribeye by design. Same trim caveat applies; confirm FDC record.',
  },

  {
    id: 'costco-beef-stick',
    name: 'Kirkland Grass-Fed Beef Stick',
    category: 'Beef',
    basis: 'as-is',
    confidence: 'verified',
    per100g: { kcal: 313, protein: 31.3, fat: 21.9, carbs: 3.1,
               sodium: 1219, potassium: 588, magnesium: null },
    units: [
      { unit: 'stick', gramsPerUnit: 32 },
      { unit: 'g', gramsPerUnit: 1 },
    ],
    defaultUnit: 'stick',
    defaultQty: 1,
    source: 'Manufacturer label, Costco ITM 1857160, photographed 2026-08-02',
    note: 'Per stick: 100 kcal, 10P, 7F, 1C, 390mg Na, ~188mg K. Magnesium not disclosed. No sugar, no seed oils — ingredient panel verified.',
  },

  {
    id: 'jerky-homemade',
    name: 'Beef Jerky (homemade, eye of round)',
    category: 'Beef',
    basis: 'as-is',
    confidence: 'estimated',
    per100g: { kcal: 410, protein: 55, fat: 18, carbs: 2,
               sodium: 1800, potassium: 800, magnesium: 45 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'g',
    defaultQty: 40,
    source: 'ESTIMATE — derived from eye of round + dehydration ratio + bacon-style cure',
    note: 'Eye of round is lean (~150 kcal/100g raw). At ~40% finished yield, protein concentrates ~2.5x. Sodium dominated by the cure and is the softest number here. Weigh green and finished once and this becomes real.',
  },

  // ---------- POULTRY ----------
  {
    id: 'chicken-wings',
    name: 'Chicken Wings (party, fried in tallow)',
    category: 'Poultry',
    basis: 'cooked',
    confidence: 'verified',
    // Calcium and retinol come from the SAME USDA FDC #173630 record already
    // sourcing this entry's macros — checked by confirming that record's
    // kcal/protein/fat/sodium/potassium/magnesium match what is already here,
    // so these two are additional fields off one row rather than a second food
    // bolted onto the first. No iodine: USDA does not measure it (see header).
    per100g: { kcal: 254, protein: 23.8, fat: 16.8, carbs: 0,
               sodium: 98, potassium: 212, magnesium: 19,
               calcium: 18, retinol: 12 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'g bone-in', gramsPerUnit: 1, requiresCount: true },
      { unit: 'wing', gramsPerUnit: 22.3 },
    ],
    boneWeightPerUnit: 14.8,
    defaultUnit: 'wing',
    defaultQty: 8,
    source: 'USDA FDC #173630 — chicken, broilers or fryers, wing, meat and skin, cooked, roasted',
    note: 'Naked, no breading, deep fried 350F in tallow to 170F internal. Unbreaded skin renders about as much as it absorbs, so roasted values hold. Restaurant Depot jumbo (BWW/Hooters grade), ~55g raw per segment. BONE AND YIELD: MEASURED 2026-08-03, not estimated. One batch, 3 wings — 111.5g cooked bone-in on the plate, 44.5g of bone after eating, 67.0g of meat by subtraction. That gives 14.8g of bone per piece (44.5/3) and 22.3g of meat per wing (67.0/3), both used above. n=3 from a single batch is NOT settled: it is one cook, one grade of wing, one operator. Re-weigh a couple more batches before treating either constant as exact. The two round-trip to within 0.1g of each other, which is a rounding artifact of carrying one decimal, not independent confirmation.',
  },

  {
    id: 'chicken-breast',
    name: 'Chicken Breast (boneless, skinless)',
    category: 'Poultry',
    basis: 'cooked',
    confidence: 'good',
    // Calcium and retinol come off the same USDA roasted-breast record as the
    // macros — one row, extra fields, same as the wings entry. No iodine:
    // USDA does not measure it (see header).
    per100g: { kcal: 165, protein: 31, fat: 3.6, carbs: 0,
               sodium: 74, potassium: 256, magnesium: 29,
               calcium: 15, retinol: 6 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
      { unit: 'lb', gramsPerUnit: 453.6 },
    ],
    defaultUnit: 'g',
    defaultQty: 250,
    source: 'USDA FDC #171077 — chicken, broilers or fryers, breast, meat only, cooked, roasted',
    note: 'Weigh COOKED, same as the beef. Raw-to-cooked yield is ~75%: 100g raw gives ~75g cooked, so weighing raw against these figures understates the plate by a third. \'good\' rather than \'verified\' for the yield ambiguity — a breast pulled early or cooked dry moves the water fraction these per-100g figures sit on.',
  },

  // ---------- FISH ----------
  {
    id: 'wild-planet-albacore',
    name: 'Wild Planet Albacore Tuna',
    category: 'Fish',
    basis: 'as-is',
    confidence: 'verified',
    // Label photographed 2026-08-06. The panel is PER CAN — 1 serving per
    // container, serving size 1 can (142g) — so each figure below is the
    // panel value divided by 1.42:
    //   kcal      180 / 1.42 = 126.760… -> 126.76
    //   protein    33 / 1.42 =  23.239… -> 23.24
    //   fat         5 / 1.42 =   3.521… -> 3.52
    //   sodium    310 / 1.42 = 218.309… -> 218.31
    //   potassium 470 / 1.42 = 330.985… -> 330.99
    // DV conversions:
    //   potassium 10% of 4700mg = 470mg per can
    //   calcium    0% of 1300mg =   0mg per can  <- DISCLOSED ZERO
    //   (vit D 90% of 20mcg = 18mcg; iron 4% of 18mg = 0.72mg. Neither has
    //   a column — both recorded in the note, neither stored.)
    //
    // calcium is an explicit 0 per the mayo precedent: a declared 0% DV is
    // a statement about the food, not a silence. magnesium, iodine and
    // retinol are NULL, NOT ZERO — none of the three is on the panel.
    //
    // The values this entry carried before 2026-08-06 were recalled from
    // memory and were WRONG against the photographed panel: fat 60% low
    // (2g vs 5g per can), protein 12% high (37g vs 33g), kcal 3% low
    // (175 vs 180).
    per100g: { kcal: 126.76, protein: 23.24, fat: 3.52, carbs: 0,
               sodium: 218.31, potassium: 330.99, calcium: 0,
               magnesium: null, iodine: null, retinol: null },
    units: [
      { unit: 'can', gramsPerUnit: 142 },
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'can',
    defaultQty: 1,
    source: 'Wild Planet Albacore Tuna package label, photographed 2026-08-06. 1 serving per can, 142 g.',
    note: 'COOKED-WEIGHT CONVENTION DOES NOT APPLY and must not be "fixed": canned tuna is already cooked and packed, so drained weight IS the weight — nothing renders, nothing to yield-adjust. The g/oz units are for weighing a partial or heavier can. Omega-3 is not declared on this panel. Manufacturer marketing claims ~1500mg EPA+DHA per can, which at 5g total fat is ~30% of lipid - plausible, but unverified and not stored. Vitamin D 90% DV = 18mcg per can and iron 4% DV = 0.72mg are on the panel but have no column. MERCURY: albacore runs higher than skipjack; FDA guidance is one serving per week.',
  },

  // ---------- PORK ----------
  {
    id: 'bacon',
    name: 'Bacon (home-cured, no sugar)',
    category: 'Pork',
    basis: 'cooked',
    confidence: 'good',
    per100g: { kcal: 541, protein: 37, fat: 42, carbs: 0,
               sodium: 1500, potassium: 565, magnesium: 32 },
    units: [
      { unit: 'slice', gramsPerUnit: 12 },
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'slice',
    defaultQty: 4,
    source: 'USDA SR — pork, cured, bacon, cooked, pan-fried (macros); carbs 0 and sodium derived from Marc\'s cure: 1.5% salt + 0.25% Prague #1 of green weight, no sugar',
    note: 'Home-cured, no sugar. Sodium is derived from the cure rate, not measured. Weigh after cooking; fat and calories drop as it gets crispier.',
  },
  {
    id: 'canadian-bacon-homemade',
    name: 'Canadian Bacon (home-cured, smoked)',
    category: 'Pork',
    basis: 'as-is',
    confidence: 'estimated',
    per100g: { kcal: 165, protein: 30, fat: 5, carbs: 0,
               sodium: 1000, potassium: 480, magnesium: 28 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'g',
    defaultQty: 80,
    source: 'Derived: lean pork loin ÷ 75% cure+smoke yield; cure 1.5% salt + 0.25% Prague #1 of green weight, no sugar',
    note: 'Weigh sliced, as finished. Sodium and protein ride on the 75% yield estimate — weigh green vs. smoked once to correct.',
  },

  // ---------- EGGS ----------
  //
  // COOKED-WEIGHT CONVENTION DOES NOT APPLY to any entry in this section,
  // and that is a decision, not an oversight: raw and cooked egg are
  // nutritionally identical unless cooking fat is added, and the fat is
  // logged separately as Beef Tallow. Applying a cooked yield factor here
  // would double-adjust. Do not "fix" this to match the meat entries.
  {
    id: 'egg-whole-large',
    name: 'Egg, Large, Whole',
    category: 'Eggs',
    basis: 'as-is',
    confidence: 'good',
    // Per large egg, 50g edible: 72 kcal, 6.3P, 4.8F, 0.4C, 71mg Na,
    // 69mg K, 6mg Mg, 28mg Ca, 80mcg retinol, 24mcg iodine. Each figure
    // below is that times 2 (50g -> 100g), and lands on the USDA
    // whole-raw-egg record.
    per100g: { kcal: 144, protein: 12.6, fat: 9.6, carbs: 0.8,
               sodium: 142, potassium: 138, magnesium: 12,
               calcium: 56, retinol: 160, iodine: 48 },
    units: [
      { unit: 'each', gramsPerUnit: 50 },
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'each',
    defaultQty: 3,
    source: 'USDA FDC #171287 — egg, whole, raw, fresh; per-egg figures are the 50g edible portion',
    note: 'Weigh raw or cooked, same numbers — see the section header for why no yield factor applies. Scrambling several? The g unit is for weighing the finished amount instead of counting. RETINOL: 80mcg per egg makes this the only meaningful vitamin A source in the catalog. IODINE: 24mcg per egg, essentially all of it in the yolk — after iodized salt, eggs are the only meaningful iodine source in this catalog.',
  },

  {
    id: 'egg-white-large',
    name: 'Egg White, Large',
    category: 'Eggs',
    basis: 'as-is',
    confidence: 'good',
    // Per large white, 33g: 17 kcal, 3.6P, 0.06F, 0.2C, 55mg Na, 54mg K,
    // 4mg Mg. Each figure below is that times 100/33:
    //   kcal    17  × 100/33 =  51.515… -> 51.52
    //   protein 3.6 × 100/33 =  10.909… -> 10.91
    //   fat     0.06× 100/33 =   0.181… -> 0.18
    //   carbs   0.2 × 100/33 =   0.606… -> 0.61
    //   sodium  55  × 100/33 = 166.66…  -> 166.67
    //   potassium 54× 100/33 = 163.63…  -> 163.64
    //   magnesium  4× 100/33 =  12.12…  -> 12.12
    // Calcium and retinol carry no key: not specified, absent = undisclosed.
    // Iodine is a REAL DISCLOSED ZERO, not an omission — the white genuinely
    // has none (it rides the yolk), and 0 vs undisclosed must stay
    // distinguishable, same as the salt entries.
    per100g: { kcal: 51.52, protein: 10.91, fat: 0.18, carbs: 0.61,
               sodium: 166.67, potassium: 163.64, magnesium: 12.12,
               iodine: 0 },
    units: [
      { unit: 'each', gramsPerUnit: 33 },
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'each',
    defaultQty: 4,
    source: 'USDA FDC #171288 — egg, white, raw, fresh; per-white figures are the 33g large portion',
    note: 'SELF-CHECK — white + yolk should sum to approximately the whole egg, and on the money numbers it does exactly: 17+55 = 72 kcal, 3.6+2.7 = 6.3g protein, 33+17 = 50g. This is the catalog\'s second self-verifying entry, after the wing bone/meat round-trip. The check is APPROXIMATE off those two: fat sums to 4.56 vs 4.8 and carbs to 0.8 vs 0.4, because USDA\'s whole/white/yolk records are measured independently and disagree with each other at that scale — not a transcription error here.',
  },

  {
    id: 'egg-yolk-large',
    name: 'Egg Yolk, Large',
    category: 'Eggs',
    basis: 'as-is',
    confidence: 'good',
    // Per large yolk, 17g: 55 kcal, 2.7P, 4.5F, 0.6C, 8mg Na, 19mg K,
    // 1mg Mg, 22mg Ca, 65mcg retinol. Each figure below is that times 100/17:
    //   kcal    55  × 100/17 = 323.529… -> 323.53
    //   protein 2.7 × 100/17 =  15.882… -> 15.88
    //   fat     4.5 × 100/17 =  26.470… -> 26.47
    //   carbs   0.6 × 100/17 =   3.529… -> 3.53
    //   sodium    8 × 100/17 =  47.058… -> 47.06
    //   potassium 19× 100/17 = 111.76…  -> 111.76
    //   magnesium  1× 100/17 =   5.88…  -> 5.88
    //   calcium   22× 100/17 = 129.41…  -> 129.41
    //   retinol   65× 100/17 = 382.35…  -> 382.35
    //   iodine    24× 100/17 = 141.17…  -> 141.18   (the whole egg's 24mcg
    //             lives here — the white's is a disclosed zero, so the
    //             iodine self-check is exact: 0 + 24 = 24)
    per100g: { kcal: 323.53, protein: 15.88, fat: 26.47, carbs: 3.53,
               sodium: 47.06, potassium: 111.76, magnesium: 5.88,
               calcium: 129.41, retinol: 382.35, iodine: 141.18 },
    units: [
      { unit: 'each', gramsPerUnit: 17 },
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'each',
    defaultQty: 2,
    source: 'USDA FDC #171289 — egg, yolk, raw, fresh; per-yolk figures are the 17g large portion',
    note: 'Same self-check as the white entry, stated from the other side: 55+17 = 72 kcal, 2.7+3.6 = 6.3g protein against the whole egg — exact on both, and iodine checks exactly too (0 + 24 = 24). Fat and carbs sum only approximately (see the white entry\'s note for the USDA-records reason). The yolk carries nearly all of the egg\'s fat, calcium, retinol and iodine — after iodized salt, eggs are the only meaningful iodine source in this catalog.',
  },

  // ---------- FATS ----------
  {
    id: 'beef-tallow',
    name: 'Beef Tallow',
    category: 'Fats',
    basis: 'as-added',
    confidence: 'verified',
    // Rendered fat is essentially pure lipid, so all three are REAL DISCLOSED
    // ZEROS, in the same class as the protein and carb zeros already here — the
    // source states the absence rather than failing to mention it. They must be
    // 0 and must never become null or absent.
    per100g: { kcal: 902, protein: 0, fat: 100, carbs: 0,
               sodium: 0, potassium: 0, magnesium: 0,
               calcium: 0, retinol: 0, iodine: 0 },
    units: [
      { unit: 'tbsp', gramsPerUnit: 12.8 },
      { unit: 'tsp', gramsPerUnit: 4.3 },
      { unit: 'g', gramsPerUnit: 1 },
    ],
    defaultUnit: 'tbsp',
    defaultQty: 1,
    source: 'USDA SR — beef tallow. Pure rendered fat: 100g fat per 100g by definition.',
    note: 'Only log tallow you EAT (added to a plate, spooned over). Frying oil is captured in the fries entry, not here — logging both double-counts.',
  },

  {
    id: 'mayo-chosen-avocado',
    name: 'Mayonnaise (Chosen Foods, avocado oil)',
    category: 'Fats',
    basis: 'as-added',
    // 'verified' matches the tier every other photographed-package-label entry
    // uses: cheddar-cabot-3yr, salt-iodized, salt-kosher.
    confidence: 'verified',
    // Label is per 1 Tbsp (14g); every figure below is that scaled to 100g:
    //   kcal    100 / 14 * 100 = 714.285… -> 714.29
    //   fat      11 / 14 * 100 =  78.571… -> 78.57
    //   sodium   50 / 14 * 100 = 357.142… -> 357.14
    //   protein and carbs are label zeros.
    //
    // potassium and calcium are DISCLOSED ZEROS: the panel affirmatively
    // declares both "not a significant source", which is a statement about
    // the food, not a silence. magnesium, iodine and retinol are NULL, NOT
    // ZERO — none of the three appears on the panel and none is covered by
    // the not-significant statement (egg yolks contribute trace retinol, but
    // the label does not say how much). Same mixed shape as cheddar-cabot-3yr.
    per100g: { kcal: 714.29, protein: 0, fat: 78.57, carbs: 0,
               sodium: 357.14, potassium: 0, calcium: 0,
               magnesium: null, iodine: null, retinol: null },
    units: [
      { unit: 'tbsp', gramsPerUnit: 14 },
      { unit: 'g', gramsPerUnit: 1 },
    ],
    defaultUnit: 'tbsp',
    defaultQty: 2,
    source: 'Chosen Foods avocado oil mayonnaise package label, photographed 2026-08-06',
    note: 'Package label, not USDA. 1 Tbsp (14g) = 100 cal / 11g fat / 0 protein / 0 carb / 50mg sodium. Panel declares potassium and calcium not significant (recorded as disclosed 0). Magnesium, iodine and retinol absent from label (undisclosed). Mono 8g, poly 1.5g, sat 1.5g of the 11g fat.',
  },

  // ---------- DAIRY ----------
  {
    id: 'cheddar-cabot-3yr',
    name: 'Cheddar (Cabot 3-Year)',
    category: 'Dairy',
    basis: 'as-is',
    confidence: 'verified',
    // Label is per 1 oz (28g); every figure below is that scaled to 100g:
    //   kcal      110 / 28 * 100 = 392.857… -> 392.86
    //   protein     7 / 28 * 100 = 25
    //   fat         9 / 28 * 100 = 32.142… -> 32.14
    //   carbs       1 / 28 * 100 = 3.571…  -> 3.57
    //   sodium    200 / 28 * 100 = 714.28… -> 714.29
    //   potassium  20 / 28 * 100 = 71.428… -> 71.43
    //   calcium   194 / 28 * 100 = 692.857… -> 692.86
    //
    // magnesium, retinol and iodine are NULL, NOT ZERO. Cheese certainly
    // contains magnesium and retinol; the label simply does not declare them,
    // and a 0 here would assert their absence as fact. This is the same case as
    // costco-beef-stick's magnesium — the mixed entry that proves the omission
    // path works on real data rather than only on a synthetic probe fixture.
    per100g: { kcal: 392.86, protein: 25, fat: 32.14, carbs: 3.57,
               sodium: 714.29, potassium: 71.43, calcium: 692.86,
               magnesium: null, retinol: null, iodine: null },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'g',
    defaultQty: 28,
    source: 'Manufacturer label: 1 oz (28g) = 110 cal, 9g fat, 7g protein, 1g carb, 200mg sodium, 194mg calcium, 20mg potassium',
    note: 'Label figures, confirmed across three independent retail listings all agreeing. Magnesium, retinol and iodine are NOT disclosed on the label — null, not zero. Sodium, potassium and calcium are label values. ID WARNING: this entry was created AFTER a row was already logged against FoodId \'cheddar-cabot-3yr\' via MCP on 2026-08-03. The id must not change or that row is orphaned from the catalog with nothing to point it back. Note also the unit mismatch the label itself carries: it calls 1 oz 28g, while the oz unit here is the true 28.35g — logging by the oz unit therefore yields slightly more than one labelled serving.',
  },

  // ---------- BROTH ----------
  {
    id: 'chicken-stock',
    name: 'Chicken Stock (homemade, gelatinous)',
    category: 'Broth',
    basis: 'as-is',
    confidence: 'estimated',
    per100g: { kcal: 38, protein: 7, fat: 1, carbs: 0,
               sodium: 120, potassium: 90, magnesium: 6 },
    units: [
      { unit: 'cup', gramsPerUnit: 240 },
      { unit: 'fl oz', gramsPerUnit: 30 },
      { unit: 'g', gramsPerUnit: 1 },
    ],
    defaultUnit: 'cup',
    defaultQty: 1,
    source: 'ESTIMATE — no USDA analog. Commercial broth records (~10 kcal/cup) describe thin stock and would badly understate a stock that gels.',
    note: 'Gels cold = high collagen. Estimated ~17g protein per cup. Sodium depends entirely on whether you salt the pot — adjust if you do. Per cup: ~91 kcal, 17P.',
  },

  {
    id: 'chicken-stock-reduction',
    name: 'Stock Reduction (sauce base)',
    category: 'Broth',
    basis: 'as-is',
    confidence: 'estimated',
    per100g: { kcal: 152, protein: 28, fat: 4, carbs: 0,
               sodium: 480, potassium: 360, magnesium: 24 },
    units: [
      { unit: 'tbsp', gramsPerUnit: 15 },
      { unit: 'fl oz', gramsPerUnit: 30 },
      { unit: 'g', gramsPerUnit: 1 },
    ],
    defaultUnit: 'tbsp',
    defaultQty: 3,
    source: 'ESTIMATE — chicken-stock entry concentrated 4x by volume',
    note: 'Separate entry because reduction concentrates everything. A cup down to a quarter = 4x per fl oz. If you reduce harder or softer, this is wrong in the obvious direction. Sodium scales with it and gets real fast.',
  },

  // ---------- ELECTROLYTES ----------
  {
    id: 'lmnt',
    // Renamed from 'LMNT (1 stick)' when the Lemonade Salt entry landed. The id
    // stays 'lmnt': it is the FoodId on every row already logged, and changing
    // it would orphan them from the catalog with nothing to point them back.
    name: 'LMNT (Raw Unflavored)',
    category: 'Electrolytes',
    basis: 'as-added',
    confidence: 'good',
    // The label declares no calcium, and an electrolyte salt blend carries no
    // retinol or iodine. Disclosed zeros, not unknowns.
    per100g: { kcal: 0, protein: 0, fat: 0, carbs: 0,
               sodium: 16667, potassium: 3333, magnesium: 1000,
               calcium: 0, retinol: 0, iodine: 0 },
    units: [
      { unit: 'stick', gramsPerUnit: 6 },
    ],
    defaultUnit: 'stick',
    defaultQty: 1,
    source: 'Manufacturer label — 1000mg sodium, 200mg potassium, 60mg magnesium per stick',
    note: 'Zero macros. Exists in the catalog purely to make the mineral totals honest. Confirm against a packet — label values are from spec, not photographed.',
  },

  {
    id: 'lmnt-lemonade',
    name: 'LMNT (Lemonade Salt)',
    category: 'Electrolytes',
    // Matches the Raw entry on both, deliberately. 'as-added' because LMNT is
    // dissolved in water rather than eaten as it sits, so nothing goes on the
    // scale in the state this describes. 'good' rather than 'verified' because
    // the figures come from published spec and a marketing box, not from a
    // photographed nutrition panel — the same reservation the Raw entry's note
    // already records. Two entries for one product must not disagree about the
    // provenance of the same label.
    basis: 'as-added',
    confidence: 'good',
    // Identical to the Raw Unflavored entry except for the two carbohydrate-
    // derived figures. Per 6g stick the label gives 1g of carbs, so:
    //   carbs 1 / 6 * 100     = 16.666… -> 16.7
    //   kcal  4 / 6 * 100     = 66.666… -> 66.7   (1g carb at 4 kcal/g)
    // Both carried to one decimal, matching how the mineral figures on the Raw
    // entry are rounded (16667, 3333). A 6g stick therefore reads 1.002g carbs
    // and 4.002 kcal, which is the rounding residue, not a second measurement.
    // Same as Raw Unflavored: label declares no calcium, and the blend carries
    // no retinol or iodine. Disclosed zeros.
    per100g: { kcal: 66.7, protein: 0, fat: 0, carbs: 16.7,
               sodium: 16667, potassium: 3333, magnesium: 1000,
               calcium: 0, retinol: 0, iodine: 0 },
    units: [
      { unit: 'stick', gramsPerUnit: 6 },
    ],
    defaultUnit: 'stick',
    defaultQty: 1,
    source: 'LMNT package: 1000mg sodium, 200mg potassium, 60mg magnesium, 1g carbs per stick',
    note: 'Same electrolyte load as Raw Unflavored — sodium, potassium and magnesium are identical per stick. The only difference is 1g of carbohydrate from the flavoring, about 4 calories. Kept as a SEPARATE ENTRY rather than a flavor option on the existing one because the unit dropdown means amount, not variant; overloading it with flavors would collide with the requiresCount logic, which keys off the selected unit object. Protein and fat are REAL disclosed zeros from a manufactured product\'s label, not unknowns. PROVENANCE: the 1g carb figure is from spec, same as the minerals — not read off a nutrition panel. Both LMNT entries should be re-confirmed against an actual photographed label when one is to hand, and promoted to \'verified\' together or not at all.',
  },

  {
    id: 'salt-iodized',
    name: 'Salt (Morton iodized)',
    category: 'Electrolytes',
    basis: 'as-is',
    confidence: 'verified',
    // iodine 4500 = 67.5 mcg per 1.5g serving scaled to 100g. This is the one
    // entry in the catalog with a real iodine figure, and it is a LABEL value —
    // USDA measures no iodine at all (see header). Calcium and retinol are
    // disclosed zeros: table salt contains neither and the label says so.
    per100g: { kcal: 0, protein: 0, fat: 0, carbs: 0,
               sodium: 39333, potassium: 0, magnesium: 0,
               calcium: 0, retinol: 0, iodine: 4500 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'tsp', gramsPerUnit: 6 },
    ],
    defaultUnit: 'g',
    defaultQty: 2,
    source: 'Morton Iodized Salt package label: 1/4 tsp (1.5g) = 590mg sodium, 67.5mcg iodine (45% DV)',
    note: 'Every zero here is a REAL disclosed zero, not an unknown — table salt genuinely contains no calories, protein, fat, carbs, potassium or magnesium. They must stay 0 and must never become null, or the day\'s totals would report those nutrients as undisclosed on a food whose composition is fully known. IODINE: 4500 mcg per 100g (67.5mcg per 1.5g serving), live in per100g and written to the IodineMcg column on every logged row. Cross-checked: the label works out to 45 mcg iodine per gram of salt, which matches independent published figures for US iodized salt. defaultUnit is grams rather than tsp because seasoning is done by percentage of food weight.',
  },

  {
    id: 'salt-kosher',
    name: 'Salt (kosher, non-iodized)',
    category: 'Electrolytes',
    basis: 'as-is',
    confidence: 'verified',
    // Identical to salt-iodized on every field EXCEPT iodine, which is a real
    // disclosed zero here rather than 4500 — that single number is the entire
    // nutritional difference between the two entries.
    per100g: { kcal: 0, protein: 0, fat: 0, carbs: 0,
               sodium: 39333, potassium: 0, magnesium: 0,
               calcium: 0, retinol: 0, iodine: 0 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'tsp', gramsPerUnit: 4.8 },
    ],
    defaultUnit: 'g',
    defaultQty: 2,
    source: 'Sodium chloride is 39.3% sodium by weight; matches Morton coarse kosher label',
    note: 'Chemically the same salt as the iodized entry BY WEIGHT — sodium per gram is identical, and so is every other per100g field except one. The only nutritional difference is iodine: 4500 mcg/100g there, a disclosed 0 here. The tsp figure differs (4.8g against 6g) because kosher crystals are coarser and pack less densely per spoon; that difference vanishes entirely when measuring by weight, which is the intended use. As above, every zero is a disclosed zero.',
  },

  // ---------- PLANTS ----------
  {
    id: 'banana',
    name: 'Banana',
    category: 'Plants',
    basis: 'as-is',
    confidence: 'verified',
    per100g: { kcal: 89, protein: 1.09, fat: 0.33, carbs: 22.84,
               sodium: 1, potassium: 358, magnesium: 27,
               calcium: 5, retinol: 0 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'small', gramsPerUnit: 101 },
      { unit: 'medium', gramsPerUnit: 118 },
      { unit: 'large', gramsPerUnit: 136 },
    ],
    defaultUnit: 'medium',
    defaultQty: 1,
    source: 'USDA FDC #173944 — Bananas, raw',
    note: 'Peeled weight. Retinol 0 — banana carries beta-carotene, not preformed vitamin A.',
  },

  // ---------- OFF-PLAN ----------
  {
    id: 'french-fries-tallow',
    name: 'French Fries (homemade, tallow, double-fried)',
    category: 'Off-Plan',
    basis: 'cooked',
    confidence: 'estimated',
    per100g: { kcal: 274, protein: 3.4, fat: 14, carbs: 34, sodium: 8,
               potassium: 580, magnesium: 22 },
    units: [
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'g',
    defaultQty: 150,
    source: 'ESTIMATE — russet potato base + 10% absorbed tallow by finished weight',
    note: 'Par-fry, freeze, finish — all in beef tallow. USDA home-prepared fries assume vegetable oil, so the fat QUALITY differs even where grams match. Absorption of 10% is the soft number; 8-12% is the plausible range. Weigh a batch before and after frying once to pin it. Salt not included — add LMNT or salt separately if you track it.',
  },

  {
    id: 'taco-shell-hard',
    name: 'Taco Shell (hard, corn)',
    category: 'Off-Plan',
    basis: 'as-is',
    confidence: 'good',
    per100g: { kcal: 500, protein: 7.7, fat: 23.1, carbs: 65.4,
               sodium: 500,
               retinol: 0 },
    units: [
      { unit: 'shell', gramsPerUnit: 13 },
      { unit: 'g', gramsPerUnit: 1 },
      { unit: 'oz', gramsPerUnit: 28.35 },
    ],
    defaultUnit: 'shell',
    defaultQty: 1,
    source: 'Old El Paso crunchy taco shell label - 2 shells (26 g) = 130 cal, 6 g fat, 17 g carb, 2 g protein, 130 mg sodium',
    note: 'Potassium, magnesium, calcium and iodine ABSENT - not on the label and no clean USDA record for a fried corn shell. Retinol is a disclosed 0: plant foods carry no preformed vitamin A. The 13 g shell weight is derived from the 2-shell serving weight, not weighed. Fried in seed oil (soybean or canola) - the fat here is not comparable to the rest of the catalog.',
  },

];

export default foodData;
